import { get, put, patch, postJson, postFormData, deleteRequest } from "./apiClient";
import type { UnitConversion } from "../utils/unitConversions";
import type { PackStructure } from "../utils/packConversion";

export interface MasterCatalogueItem {
  _id: string;
  item_name: string;
  last_updated: string;
  category?: string | null;
  sub_category?: string | null;
  source: 'quotation_historical' | 'vendor_invoice' | 'price_list' | 'manual' | 'unknown';
  item_price?: number | null;
  unit?: string | null;
  /**
   * Whether VAT applies to this row's cost. `true`/`false` price it as usual
   * (`costPriceTiers.catalogueRowCostPrice`, always at the Settings rate —
   * see `vat_rate` below). `null`/`undefined` — BLANK, nobody has said —
   * prices the line WITHOUT VAT and marks it
   * (`pricingDetails.catalogueVatUnset`) for the "Does this item have VAT?"
   * popup (`features/quotations/CatalogueVatPrompt.tsx`), which answers it
   * through `setCatalogueVat` below.
   */
  vat_applicable?: boolean | null;
  /**
   * The VAT rate this row states, percent — server field, `models/MasterCatalogue.js`.
   * NO LONGER READ for cost (owner decision 2026-09-24, "one Trinidad VAT
   * rate"): `catalogueRowCostPrice` grosses a `vat_applicable` row at the one
   * Settings rate (`lib/settingsApi.getVatRate`) always, never at what this
   * field states. Kept on the type only because the server model still
   * carries it.
   */
  vat_rate?: number | null;
  finalPrice?: number | null;
  /**
   * D86, `/master-catalogue/search` only: ISO date this row's price counts from
   * (server `priceDateOf`, computed on read), or null when nothing dates it.
   */
  priceDate?: string | null;
  /** What dated that price: 'invoice' | 'price_list' | 'added' | 'price_edit', or null. Read by `priceSourceLabel`. */
  priceDateBasis?: string | null;
  /**
   * The VAT percent inside this row's cost, worked out by the server owner
   * (`masterCatalogueVendorSync.catalogueRowCostVatPercent`) at the Settings
   * rate of the request that served the row: 0 = No, null = blank (nothing
   * added) or a Yes row with no rate set (no cost either). A quotation line
   * priced from this row records it as `pricingDetails.costVatPercent`
   * (D82) — never recomputed here.
   */
  costVatPercent?: number | null;
  /** Fresh produce, priced monthly by hand: a bill only proposes its price (D170.6). The Edit Item tick. */
  is_fresh?: boolean;
  tags?: string[];
  keywords?: string[];
  meta_data?: Record<string, any>;
  supplier?: string | null;
  /** The vendor record `supplier` names, when the register settled it (null for a legacy typed name). */
  vendorId?: string | null;
  currency?: string | null;
  /**
   * WHICH vendor's WHICH product this row is — the key that makes a second
   * invoice of the same goods move this row's price instead of adding a row
   * beside it. Server-derived (`utils/catalogueIdentity.js`); never computed
   * here, and never sent back.
   *
   * Null means the row has no derivable identity (no settleable vendor, or a
   * name with no identifying token) and is still one-row-per-invoice-line.
   */
  identityKey?: string | null;
  /**
   * The supplier's own item code, on a row of a supplier whose code IS the
   * product (PriceSmart — server `catalogueIdentity.CODE_IDENTIFIED_SUPPLIERS`).
   * Shown in brackets beside the item name; null on every other row. Written by
   * the server (the vendor sync); never sent back.
   */
  vendorItemCode?: string | null;
  /**
   * What `item_price` moved from, and the invoice that stated it. Reported in
   * the currency it was invoiced in — NOT restated in TT$, because converting a
   * superseded price at today's rate prints a figure no document ever stated.
   */
  previousPrice?: {
    amount?: number | null;
    currency?: string | null;
    invoiceNumber?: string | null;
    invoiceDate?: string | null;
    /** The vendor invoice that stated it, by id (D119.2) - what the number links to. Null: no vendor invoice did. */
    invoiceId?: string | null;
    at?: string | null;
  } | null;
  /**
   * A price change held back for a person to look at: a supplier's new price
   * that moved more than the re-price line (`utils/repricePolicy`, 25%), or one that would have overwritten a figure
   * somebody typed. The row keeps the price it has until then.
   *
   * NOT a price this row holds — never format it like one.
   */
  pendingReprice?: {
    fromPrice?: number | null;
    toPrice: number;
    currency?: string | null;
    changePct?: number | null;
    reason?: string;
    proposedAt?: string;
    /** The vendor invoice that proposed it, by id and line (D119.2). Null on a quotation-harvest proposal. */
    invoiceId?: string | null;
    invoiceLineIndex?: number | null;
  } | null;
  /**
   * TT$-native price tiers worked out once when the source invoice was processed,
   * using that invoice's own FX rate, and VAT-inclusive where VAT applies. Prefer
   * these over recomputing from `item_price` — see MasterCatalogue.js (MC3).
   * Absent on rows written before this existed and on grouped (`isGroup`) rows.
   */
  priceLevels?: {
    ttPerLevel2?: number;
    ttPerLevel1?: number;
    ttPerKg?: number;
    ttPerLb?: number;
    /** Per litre — null for anything not sold by volume (prod USC-BUG-0013 / 0014 / 0018). */
    ttPerL?: number | null;
    usdPerLevel2?: number;
    usdPerLevel1?: number;
    usdPerKg?: number;
    usdPerLb?: number;
    usdPerL?: number | null;
    vatInclusive?: boolean;
    /**
     * The same money, at EVERY unit `unitConversions` can reach — the four tiers
     * above can only describe a two-level pack. A superset: the units those four
     * cover appear here too, each entry naming the unit it prices.
     *
     * Empty when the row states no price to spread, and a unit whose price
     * rounds away to nothing at cent precision is OMITTED rather than stored as
     * 0 — so an absent unit means "not derivable", never "free".
     */
    byUnit?: Array<{ unit: string; ttAmount: number; usdAmount: number }>;
  } | null;
  /**
   * Conversions a vendor invoice stated that CONTRADICT what this row already
   * says. The sync skips such a row rather than overwriting good data, so this
   * is how a human learns it happened. A snapshot — recomputed on every sync, so
   * a conflict the next invoice resolves stops being reported.
   */
  conversionConflicts?: Array<{
    row?: {
      fromQty?: number | null;
      fromUnit?: string | null;
      toQty?: number | null;
      toUnit?: string | null;
      source?: string | null;
    };
    message: string;
    detectedAt?: string;
  }>;
  /** The queryable half of `conversionConflicts` — the "Needs review" filter reads this. */
  hasConversionConflict?: boolean;
  /**
   * Everything the item's packaging states, as the conversions it means:
   * "1 CASE = 12 PCS", "1 PCS = 300 G". This is the record; the two fields below
   * are two readings of it, derived server-side on every write.
   *
   * Empty on rows written before it existed —
   * `utils/unitConversions.conversionsForItem` reads those off the two columns.
   */
  unitConversions?: UnitConversion[];
  /** Number of inner packs/units in one case — the HAND-ENTRY column, null on every vendor-invoice row. */
  units_per_case?: number | null;
  /** Size of one inner unit as text, e.g. "300g", "500ml", "5lb". Hand-entry, same caveat. */
  unit_size?: string | null;
  /**
   * The pack facts this row STATES, resolved server-side — read these, not the
   * two hand-entry columns above.
   *
   * `units_per_case` and `unit_size` are what a person typed into the catalogue
   * form, and they are null on the ~98% of rows that came off a vendor invoice,
   * whose pack facts live in `meta_data.vendorLineSplits` or in the pack code
   * sitting in the unit column ("C-12" is a case of twelve). A client reading
   * only the two columns answers "no pack size" for almost the whole catalogue,
   * which is exactly why the Per Pack / Per kg / Per lb cells printed "—"
   * everywhere (TECH_DEBT #138).
   *
   * ONE ranking decides this, on the server, in
   * `masterCatalogueVendorSync.mapRowToAiCandidate` — a person typing into the
   * catalogue outranks a person typing into an invoice, which outranks a count
   * recovered from a junk unit string. Never re-rank it here.
   *
   * Attached by the list, search and by-id endpoints. Absent on the Excel export
   * and on collapsed group rows.
   */
  packFacts?: {
    /** Packs in one of this row's units, or null when the row states none. Never 0. */
    packCount?: number | null;
    /** The size of ONE pack as text, e.g. "1kg" — `unit_size`, else the invoice's. */
    unitSize?: string | null;
  } | null;
  /**
   * What a blank Case Packaging cell prints instead (MC-3): "No unit size", "No pack count",
   * "No pack count or unit size", or "Packaging not in unit conversions". Built server-side
   * from `packFacts` by `masterCatalogueItem.service.packagingGapFor` — print it, never rebuild
   * it. Attached wherever `packFacts` is; absent on collapsed group rows.
   */
  packagingGap?: string | null;
  /**
   * The pack this row states — pieces to its outer pack, and what one piece
   * weighs or holds — in the shape `utils/packConversion` reads; null when the
   * row states neither. Built server-side by
   * `quotationPackStructure.packStructureOfCatalogueRow`, the same owner that
   * rebuilds a quotation line's pack from its row. Never rebuild it here.
   *
   * Attached by the search AND get-by-id endpoints, for the quotation page's
   * catalogue popup and its VAT-answer re-fetch: a pick there that changes the
   * line's unit restates the quantity through it (QUO-00054 — 20 CASE of water
   * became 20 L without it).
   */
  packStructure?: PackStructure | null;
  /**
   * The packaging as the rest of the system spells it. Read here only for
   * `level2Name`, the outer pack's name, which is what seeds a legacy row's case
   * conversion with the name the vendor sync recorded instead of a bare "CASE".
   */
  uomMeta?: {
    level2Name?: string;
    level1Name?: string;
    level1PerLevel2?: number;
    weightUnit?: 'kg' | 'lb' | null;
    weightPerLevel1?: number | null;
    volumeUnit?: 'ml' | null;
    volumePerLevel1?: number | null;
  } | null;
  origin?: {
    docId?: string;
    docCollection?: string;
    lineIndex?: number;
  };
  /** Set when a person typed the current price: no invoice stated it, so its Invoice # links to none (D119.2). */
  priceSetByHand?: { at?: string | null; by?: string | null } | null;
  createdAt?: string;
  updatedAt?: string;

  // ── Assorted-item grouping (ASSORTED_ITEM_GROUPING_PLAN.md) ──
  /** The group this row rolls up into; null when the item stands alone. */
  itemGroupId?: string | null;
  group?: { _id: string; name: string; slug: string } | null;
  /** AI proposed a group but nobody has accepted it yet. */
  suggestedGroup?: { _id: string; name: string; slug: string } | null;
  groupAssignment?: {
    method?: 'rule' | 'ai' | 'manual' | '';
    confidence?: number | null;
    locked?: boolean;
    factor?: number;
    uomWarning?: string;
    suggestedGroupId?: string | null;
  };

  // Present only on rows returned by the grouped view (`grouped=true`), where
  // several catalogue rows are collapsed into one client-facing line.
  isGroup?: boolean;
  memberCount?: number;
  suppliers?: string[];
  vatMixed?: boolean;
  priceStrategy?: string;
  priceSpread?: { min: number; max: number } | null;
  members?: Array<{
    _id: string;
    item_name: string;
    supplier?: string | null;
    item_price?: number | null;
    unit?: string | null;
    vat_applicable?: boolean | null;
    last_updated?: string;
    factor?: number;
    uomWarning?: string;
  }>;
}

export interface MasterCatalogueListResponse {
  success: boolean;
  data: MasterCatalogueItem[];
  /** True when rows are collapsed group lines rather than raw catalogue rows. */
  grouped?: boolean;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface MasterCatalogueSearchResponse {
  success: boolean;
  data: MasterCatalogueItem[];
}

export interface FilterOptions {
  success: boolean;
  data: {
    sources: string[];
    categories: (string | null)[];
    subCategories: (string | null)[];
  };
}

export interface StatisticsResponse {
  success: boolean;
  data: {
    overall: Record<string, any>;
    bySource: Array<{
      _id: string;
      count: number;
      avgPrice?: number;
    }>;
  };
}

export async function getMasterCatalogueItems(params: {
  page?: number;
  limit?: number;
  search?: string;
  source?: string;
  category?: string;
  sortBy?: string;
  sortOrder?: number;
  /** Collapse each assorted-item group into a single client-facing row. */
  grouped?: boolean;
  /** A group id, or 'any' / 'none' to filter by membership. */
  itemGroup?: string;
  /** Only rows where a vendor invoice stated a conversion that contradicts the row. */
  hasConversionConflict?: boolean;
  /** Only rows whose Case Packaging states nothing (D29 / MC-5, USC-BUG-0029). Read-only. */
  needsPackagingReview?: boolean;
  /** Only rows whose case reads as one item of a size that is really several ("Soft Drinks" CASE 330 ML). Read-only. */
  looksLikeOneItem?: boolean;
  /**
   * Exactly these rows — one quotation line's catalogue options. Sent even when
   * empty: the server reads a present-but-empty list as "no rows", never as "no
   * filter".
   */
  ids?: string[];
}): Promise<MasterCatalogueListResponse> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page || 1));
  qs.set('limit', String(params.limit || 50));
  if (params.ids) qs.set('ids', params.ids.join(','));
  if (params.search) qs.set('search', params.search);
  if (params.source) qs.set('source', params.source);
  if (params.category) qs.set('category', params.category);
  if (params.grouped) qs.set('grouped', 'true');
  if (params.itemGroup) qs.set('itemGroup', params.itemGroup);
  if (params.hasConversionConflict) qs.set('hasConversionConflict', 'true');
  if (params.needsPackagingReview) qs.set('needsPackagingReview', 'true');
  if (params.looksLikeOneItem) qs.set('looksLikeOneItem', 'true');
  qs.set('sortBy', params.sortBy || '_id');
  qs.set('sortOrder', String(params.sortOrder || -1));

  return get<MasterCatalogueListResponse>(`/master-catalogue?${qs.toString()}`);
}

export async function searchMasterCatalogue(q: string, limit: number = 20): Promise<MasterCatalogueSearchResponse> {
  const qs = new URLSearchParams();
  qs.set('q', q);
  qs.set('limit', String(limit));
  return get<MasterCatalogueSearchResponse>(`/master-catalogue/search?${qs.toString()}`);
}

/** One line of a searched invoice that is not listed under its number, and why (NEW-5). */
export interface InvoiceCoverageLine {
  lineNo: number;
  reason: string;
  /** The server's sentence, printed as it comes. Never matched on. */
  message: string;
  /** The catalogue row the line resolves to, when there is one. */
  row: { id: string; itemName: string | null; invoiceNumber: string | null } | null;
}

export interface InvoiceCoverage {
  id: string;
  invoiceNumber: string | null;
  supplier: string | null;
  invoiceDate: string | null;
  state: 'not_read' | 'not_sent_to_inventory' | 'credit_note' | 'company_expense' | 'sent';
  message: string;
  lineCount: number;
  listedCount: number;
  lines: InvoiceCoverageLine[];
}

/** Why lines of the vendor invoice(s) printed with this number are not in the grid under it. */
export async function getCatalogueInvoiceCoverage(
  invoiceNumber: string
): Promise<{ success: boolean; data: { invoices: InvoiceCoverage[] } }> {
  const qs = new URLSearchParams();
  qs.set('number', invoiceNumber);
  return get(`/master-catalogue/invoice-coverage?${qs.toString()}`);
}

export async function getMasterCatalogueItem(id: string): Promise<{ success: boolean; data: MasterCatalogueItem }> {
  return get<{ success: boolean; data: MasterCatalogueItem }>(`/master-catalogue/${id}`);
}

export async function getFilterOptions(): Promise<FilterOptions> {
  return get<FilterOptions>('/master-catalogue/filter-options');
}

export async function getStatistics(): Promise<StatisticsResponse> {
  return get<StatisticsResponse>('/master-catalogue/statistics');
}

export async function updateMasterCatalogueKeywords(
  id: string,
  keywords: string[]
): Promise<{ success: boolean; data: MasterCatalogueItem; message: string }> {
  return put<{ success: boolean; data: MasterCatalogueItem; message: string }>(
    `/master-catalogue/${id}/keywords`,
    { keywords }
  );
}

/** What a bulk keyword edit reached: `missing` counts ticked rows deleted meanwhile. */
export interface BulkKeywordsResult {
  matched: number;
  missing: number;
}

/**
 * Add and remove keywords on the ticked rows (QG-4, BUSINESS_RULES#catalogue-keyword-group).
 * The server normalises every keyword and keeps the ones nobody named.
 */
export async function bulkEditMasterCatalogueKeywords(
  ids: string[],
  add: string[],
  remove: string[]
): Promise<{ success: boolean; data: BulkKeywordsResult; message: string }> {
  return put<{ success: boolean; data: BulkKeywordsResult; message: string }>('/master-catalogue/keywords/bulk', {
    ids,
    add,
    remove,
  });
}

export interface CreateCatalogueItemPayload {
  item_name: string;
  category?: string | null;
  sub_category?: string | null;
  source?: 'quotation_historical' | 'vendor_invoice' | 'price_list' | 'manual' | 'unknown';
  item_price?: number | null;
  unit?: string | null;
  vat_applicable?: boolean | null;
  keywords?: string[];
  supplier?: string | null;
  /** The vendor picked for `supplier` (D86); the server stores that vendor's own name. */
  vendorId?: string | null;
  currency?: string | null;
  /**
   * The conversion rows. When these are sent the server DERIVES
   * `units_per_case` and `unit_size` from them and ignores whatever this payload
   * says about those two, so a row can never be saved stating one breakdown in
   * the list and another in the columns.
   */
  unitConversions?: UnitConversion[];
  units_per_case?: number | null;
  unit_size?: string | null;
  /** The Edit Item "Fresh" tick (D170.6); the add path does not read it. */
  is_fresh?: boolean;
  /**
   * D119.3: which price to keep when this save changes Units / Case on a row priced per the
   * pack. The server refuses the change (422 CATALOGUE_PACK_PRICE_CHOICE_REQUIRED, with both
   * resulting prices in `details`) until one is sent.
   */
  packPriceChoice?: PackPriceChoice;
}

export type PackPriceChoice = 'keep_pack_price' | 'keep_piece_price';

export async function createMasterCatalogueItem(
  payload: CreateCatalogueItemPayload
): Promise<{ success: boolean; data: MasterCatalogueItem; message: string }> {
  return postJson<{ success: boolean; data: MasterCatalogueItem; message: string }>(
    '/master-catalogue',
    payload
  );
}

export async function updateMasterCatalogueItem(
  id: string,
  payload: Partial<CreateCatalogueItemPayload>
): Promise<{ success: boolean; data: MasterCatalogueItem; message: string }> {
  return put<{ success: boolean; data: MasterCatalogueItem; message: string }>(
    `/master-catalogue/${id}`,
    payload
  );
}

/**
 * Answer the "Does this item have VAT?" popup for one row
 * (`features/quotations/CatalogueVatPrompt.tsx`) — the ONE writer of a row's
 * `vat_applicable` from that flow. `costPrice` on the response is the row's
 * cost at the Settings VAT rate right now (unrounded; null for a VAT row while
 * no rate is set),
 * handed back so a caller does not have to re-fetch the row to re-price a
 * line from it. `costVatPercent` is the VAT inside that cost
 * (`MasterCatalogueItem.costVatPercent`), which the line it re-prices records.
 */
export interface CatalogueVatAnswer {
  _id: string;
  vat_applicable: boolean;
  costPrice: number | null;
  costVatPercent: number | null;
}

export async function setCatalogueVat(id: string, vat_applicable: boolean): Promise<CatalogueVatAnswer> {
  return patch<{ success: boolean; data: CatalogueVatAnswer }>(
    `/master-catalogue/${id}/vat`,
    { vat_applicable }
  ).then((res) => res.data);
}

// Delete a catalogue item (QA Ticket 3)
export async function deleteMasterCatalogueItem(
  id: string
): Promise<{ success: boolean; message: string }> {
  return deleteRequest<{ success: boolean; message: string }>(`/master-catalogue/${id}`);
}

// Backward compatible: create client price list from selected items
export async function createPriceListFromCatalogue(
  name: string,
  catalogueItemIds: string[],
  markupPercent?: number
): Promise<{ success: boolean; data: { priceListId: string; createdCount: number; skippedCount: number } }> {
  return postJson('/master-catalogue/price-list', {
    name,
    itemIds: catalogueItemIds,
    markupPercent,
  });
}

// New: create price list from master catalogue (selection)
export async function createPriceListFromMasterCatalogue(payload: {
  name: string;
  itemIds: string[];
  markupPercent?: number;
}): Promise<{ success: boolean; data: { priceListId: string; createdCount: number; skippedCount: number } }> {
  return postJson('/master-catalogue/price-list', payload);
}

export interface CustomPriceList {
  _id: string;
  name: string;
  items: Array<{ itemCode?: string; description: string; packaging?: string; unit_price: number; vat: boolean }>;
  createdAt?: string;
  updatedAt?: string;
}

export async function getCustomPriceLists(params?: {
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{ success: boolean; data: CustomPriceList[]; page: number; limit: number; total: number }> {
  const qs = new URLSearchParams();
  if (params?.search) qs.set('search', params.search);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return get<{ success: boolean; data: CustomPriceList[]; page: number; limit: number; total: number }>(
    `/custom-price-lists${query ? `?${query}` : ''}`
  );
}

/** Trigger download of master catalogue Excel export. Uses current filters if provided. */
/**
 * Download the catalogue as .xlsx.
 *
 * POSTed rather than GETed so `ids` — the rows ticked in the grid — can travel
 * in the body. USC-BUG-0011: EXPORT sent only the filters, so ticking twelve
 * rows downloaded all 5,451. A selection can hold thousands of 24-character
 * ObjectIds, well past any safe URL length, so the query string was never going
 * to carry it. The GET route is still served for links built by hand.
 *
 * When `ids` is non-empty the server treats it as authoritative and ignores the
 * filters; they are still sent so one payload describes the whole request.
 */
export async function downloadMasterCatalogueExport(params?: {
  search?: string;
  source?: string;
  category?: string;
  sortBy?: string;
  sortOrder?: number;
  ids?: string[];
}): Promise<void> {
  const { API_BASE_URL } = await import('./apiClient');
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}/master-catalogue/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      search: params?.search || undefined,
      source: params?.source || undefined,
      category: params?.category || undefined,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      ids: params?.ids?.length ? params.ids : undefined,
    }),
  });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `MasterCatalogue_${Date.now()}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface ImportPreviewRow {
  index: number;
  description: string;
  unit_price: number;
  vat: boolean;
  /**
   * The packaging cell the file stated beside the price ("CASE", "PER LB").
   * Must be carried back into the commit payload or the imported row gets no
   * unit — which is exactly what happened to all 80 price-list rows on prod,
   * because this field was missing from four consecutive fixed field lists.
   */
  packaging?: string;
  /**
   * The file's own Category / Sub Category / Vendor cells, '' when it had no
   * such column (USC-BUG-0020). Carried back into the commit payload for the
   * same reason as `packaging`; the server resolves them.
   */
  category?: string;
  sub_category?: string;
  vendorName?: string;
  /** The Vendor cell names no vendor in the register: the import rejects this row (D86). */
  vendorNotInList?: boolean;
  duplicate: boolean;
  existingPrice: number | null;
  existingCurrency: string | null;
  /**
   * The matched row's `source`. Carried beside `existingCurrency` because
   * `catalogueRowIsTtd` needs both: `currency` is `default: null` on the model,
   * so on its own it reads every legacy vendor-invoice row as US$.
   */
  existingSource: string | null;
  existingSupplier: string | null;
}

/** Parse a price list file and return rows for selective import (nothing is saved). */
export async function previewMasterCatalogueImport(
  file: File
): Promise<{
  success: boolean;
  message?: string;
  data: { rows: ImportPreviewRow[]; itemsParsed: number; duplicateCount: number };
}> {
  const form = new FormData();
  form.append('file', file);
  return postFormData('/master-catalogue/import/preview', form);
}

/** Import only the selected rows from a previously previewed price list file. */
export async function commitMasterCatalogueImport(payload: {
  priceListName?: string;
  supplier?: string;
  /** The registered vendor picked for the list (QG-2); the server stores its name, not `supplier`. */
  vendorId?: string;
  items: Array<{
    description: string;
    unit_price: number;
    vat: boolean;
    packaging?: string;
    category?: string;
    sub_category?: string;
    vendorName?: string;
  }>;
}): Promise<{
  success: boolean;
  message: string;
  data?: { itemsUpserted: number; priceListName: string; supplier: string | null; vendorId: string | null };
}> {
  return postJson('/master-catalogue/import/commit', payload);
}

/** Upload a price list file (Excel, EML, or image) and merge rows into the master catalogue. */
export async function importMasterCatalogueFromFile(
  file: File,
  priceListName?: string
): Promise<{
  success: boolean;
  message: string;
  data?: { itemsParsed: number; itemsUpserted: number; priceListName: string };
}> {
  const form = new FormData();
  form.append('file', file);
  if (priceListName?.trim()) form.append('priceListName', priceListName.trim());
  return postFormData('/master-catalogue/import', form);
}
